import { FC, useEffect, KeyboardEvent } from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import { getErrorText } from 'utils/getErrorText';
import { ErrorIcon } from 'components/Core/ErorrIcon';
import { ReactComponent as StarIcon } from 'assets/star_required_icon.svg';
import { IInputProps } from './types';
import styles from './input.module.scss';

export const ControlledInput: FC<IInputProps> = (props) => {
  const {
    type,
    stylesProp,
    icon,
    name,
    label,
    errorText,
    required = false,
    isRemoveMargin = false,
    myOnChange,
    myOnFocus,
    myOnBlur,
    registerPattern,
    validation,
    showErrorIcon = true,
    classNameProp,
    min,
    max,
    iconPosition = 'left',
    autoComplete = 'off',
    ...inputProps
  } = props;
  const formContext = useFormContext();

  const { control, setValue, watch } = formContext;

  useEffect(() => {
    if (type === 'phone') {
      const phone: string | undefined = watch(name);
      phone !== undefined && phone.length > 0 && !phone.startsWith('+') && setValue(name, `+${phone}`);
    }
    if (type === 'text') {
      const text: string | undefined = watch(name);
      text && setValue(name, text.trim());
    }
  }, []);

  const valueAs = (val: string) => {
    if (type === 'money' && val) {
      if (typeof val === 'number') return val;
      const vals = val.toString().split('.');
      if (vals[1]) return +`${vals[0]}.${vals[1].slice(0, 2)}`;
      else return Number((+vals[0]).toFixed(2));
    }
    if (type === 'number' && val) {
      return +val;
    }
    if (type === 'text' && val) {
      return val.trim();
    }
    return val;
  };

  const onBlurHandler = (val: string) => {
    const valAs = valueAs(val);
    setValue(name, valAs);
    myOnBlur && myOnBlur(valAs);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      (e.target as HTMLElement).blur();
    }
  };

  return (
    <div className={styles.inputWrapper} style={isRemoveMargin ? { marginBottom: 0 } : {}}>
      {label && (
        <div className={styles.labelWrapper}>
          <p className={styles.subtitle}>{label}</p>
          {required ? <StarIcon className={styles.labelRequiredIcon} /> : null}
        </div>
      )}
      <label className={styles.label}>
        {!label && required ? <StarIcon className={styles.requiredIcon} /> : null}
        <Controller
          control={control}
          name={name}
          rules={{
            required: required,
            validate: validation?.validate,
            pattern: registerPattern,
            min: min,
            max: max,
          }}
          render={({ field, field: { onChange, value }, fieldState: { error } }) => (
            <>
              <input
                {...field}
                style={stylesProp}
                className={`${styles.input} ${icon && iconPosition === 'left' && styles.inputWithIcon} ${
                  error && styles.error
                } ${classNameProp}`}
                type={type === 'money' ? 'number' : type}
                value={value ?? ''}
                onFocus={(e) => {
                  myOnFocus && myOnFocus(e.target.value);
                }}
                onBlur={(e) => {
                  onBlurHandler(e.target.value);
                }}
                onKeyDown={onKeyDown}
                onChange={(e) => {
                  if (type === 'phone') {
                    value !== undefined && value.length > 0 && !value.startsWith('+') && onChange(`+${value}`);
                  }
                  if (myOnChange) myOnChange(e.currentTarget.value);
                  else onChange(e.currentTarget.value);
                }}
                autoComplete={autoComplete}
                {...inputProps}
              />
              {error && showErrorIcon && (
                <ErrorIcon
                  name={name}
                  message={getErrorText((error?.type as string) || '', {
                    errorText,
                    patternMsg: registerPattern?.message,
                    validateErrorText: validation?.message,
                  })}
                  stylesProp={{ top: '23%' }}
                />
              )}
            </>
          )}
        />
        {icon && <span className={`${styles.uniqueIcon} ${styles[iconPosition]}`}>{icon}</span>}
      </label>
    </div>
  );
};
